import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { useColorMode } from "@chakra-ui/react";

/* ─── Image imports ──────────────────────────────────────────────────────── */
import vg1 from "../../assets/images/newcourse.png";
import fullstack from "../../assets/images/BackendCourse.png";
import GRPFlask from "../../assets/images/GRPFlask.png";
import GRPGSAP from "../../assets/images/GRPGSAP.png";
import GRPFigma from "../../assets/images/UIUXGPTCH.png";
import GRPJava from "../../assets/images/GRPJava.png";
import GRPGit from "../../assets/images/GRPGit.png";
import GRPGenAI from "../../assets/images/GRPGenAI.png";
import BadgeAlgoAdept from "../../assets/images/Badges/AlgoAdept.png";
import BadgeCodeFusionist from "../../assets/images/Badges/CodeFusionist.png";
import BadgeTechGrandmaster from "../../assets/images/Badges/TechGrandmaster.png";
import BadgePixelCrafter from "../../assets/images/Badges/PixelCrafter.png";
import BadgeFrontendStrategist from "../../assets/images/Badges/FrontendStrategist.png";
import BadgeComponentCommander from "../../assets/images/Badges/ComponentCommander.png";
import AnimatedUnderline1 from "../Underlines/AnimatedUnderline1";

const FONT_LINK = `@import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,400;12..96,600;12..96,800&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500&display=swap');`;
const PRIMARY = "#028EC4";
const API_URL = "https://opensheet.elk.sh/13DM-Wmm0p2Ew4ovwoWOxsyfS0C8G23v0ok46_Dld3Rw/courses";

/* ─── Course data — all regular courses forced closed ────────────────────── */
const COURSE_META = [
  {
    id: "grptch09",
    title: "The Full-Stack Combo",
    image: fullstack,
    instructor: "Eshwar Nagaraj & Aruna Kumari",
    originalPrice: 3998,
    price: 999,
    rating: 4.8,
    tags: ["Combo", "React", "Backend", "Closed"],
    accentColor: "#028EC4",
    language: "Telugu + English",
    slots: 100,
    mode: "Live + Recordings",
    startDate: "April 20, 2026",
    duration: "1 Month",
    forceClosed: true,
    description:
      "ReactJS plus Complete Backend Development in one career-focused bundle. This batch is closed because all seats have already been filled.",
    curriculum: [
      {
        title: "React Week",
        desc: "Frontend foundations through a guided real-world project.",
        topics: ["Components and JSX", "Hooks and state", "Routing and APIs", "Deployment and interview prep"],
      },
      {
        title: "Backend Week",
        desc: "Build REST APIs and auth flows with real backend patterns.",
        topics: ["Node and Express", "MongoDB and Mongoose", "JWT authentication", "Deployment and interview prep"],
      },
      {
        title: "Career Add-ons",
        desc: "Everything that made the combo high value beyond the classes.",
        topics: ["LinkedIn optimisation", "Resume prep", "Mock interviews", "Interview prep kit"],
        isBonus: true,
      },
    ],
  },
  {
    id: "grptch01",
    title: "Master ReactJS in 7 Days",
    image: vg1,
    instructor: "Eshwar Nagaraj & Aruna Kumari",
    originalPrice: 1999,
    price: 799,
    rating: 4.8,
    tags: ["React", "Hooks", "JSX", "Real Projects"],
    accentColor: "#028EC4",
    language: "Telugu + English",
    slots: 100,
    mode: "Live + Recordings",
    forceClosed: true,
    description:
      "Stop tutorial-hopping. In 7 days, you'll build a real-world client project guided by the GrapplTech team — from component architecture to hooks, state, and deployment. Actually useful. Actually on your resume.",
    curriculum: [
      {
        title: "Day 1 — React Mental Model",
        desc: "Forget what you think you know. Understand why React does what it does — components, virtual DOM, and unidirectional data flow.",
        topics: ["JSX & Virtual DOM", "Component Composition", "Props vs State", "React DevTools Setup"],
      },
      {
        title: "Day 2 & 3 — Hooks Deep Dive",
        desc: "Every hook you'll use daily — with real use cases, not toy counter examples.",
        topics: ["useState & useEffect", "useRef & useCallback", "useMemo & Performance", "Custom Hooks"],
      },
      {
        title: "Day 4 — State Management",
        desc: "When to use local state, Context, Zustand — and why it actually matters.",
        topics: ["Context API", "Zustand Basics", "React Query Intro", "Derived vs Stored State"],
      },
      {
        title: "Day 5 & 6 — Real Client Project Build",
        desc: "Build a complete, deployable React app with routing, API integration, and real data — guided by the GrapplTech team.",
        topics: ["React Router v6", "Axios & Data Fetching", "Error Boundaries", "Responsive UI"],
      },
      {
        title: "Day 7 — Deploy + Interview Prep",
        desc: "Push your project live and walk into your next interview ready.",
        topics: ["Deploy on Vercel", "GitHub Portfolio Setup", "Top 60 React Interview Q&A", "Mock Interview Session"],
        isBonus: true,
      },
    ],
  },
  {
    id: "grptch02",
    title: "Complete Backend Dev in 7 Days",
    image: vg1,
    instructor: "Eshwar Nagaraj & Aruna Kumari",
    originalPrice: 1999,
    price: 799,
    rating: 4.8,
    tags: ["Node.js", "Express", "MongoDB", "JWT"],
    accentColor: "#16A34A",
    language: "Telugu + English",
    slots: 100,
    mode: "Live + Recordings",
    forceClosed: true,
    description:
      "Node.js, Express, MongoDB, JWT auth, middlewares — all in 7 days. You'll build a real backend API that actually does something. Guided project, real client use-case, deployed and on your portfolio.",
    curriculum: [
      {
        title: "Day 1 — Node.js & Express Setup",
        desc: "Build your first REST API in day one. No boilerplate mystery — understand every line.",
        topics: ["Node.js Runtime & Event Loop", "Express App Structure", "Routing & Middleware Concept", "Request / Response Lifecycle"],
      },
      {
        title: "Day 2 & 3 — MongoDB & Mongoose",
        desc: "Connect your backend to a real database. CRUD, schemas, and validation done right.",
        topics: ["MongoDB Atlas Setup", "Mongoose Models & Schemas", "CRUD Operations", "Relationships & Population"],
      },
      {
        title: "Day 4 — Auth with JWT",
        desc: "The most-asked topic in every backend interview — implement it from scratch.",
        topics: ["JWT Flow Explained", "bcrypt Password Hashing", "Auth Middleware", "Role-Based Access Control"],
      },
      {
        title: "Day 5 & 6 — Real Client Project",
        desc: "Build a full API for a real-world use-case, guided by the GrapplTech team.",
        topics: ["Input Validation (Joi/Zod)", "Global Error Handling", "Pagination & Filtering", "File Upload with Multer"],
      },
      {
        title: "Day 7 — Deploy + Interview Prep",
        desc: "Deploy on Render, set up env secrets, and go into your interview ready.",
        topics: ["Deploy on Render / Railway", "Environment Variables & Secrets", "Top 50 Backend Interview Q&A", "Mock Backend Interview"],
        isBonus: true,
      },
    ],
  },
  {
    id: "grptch03",
    title: "GSAP: Advanced Web Animations",
    image: GRPGSAP,
    instructor: "Aruna Kumari",
    originalPrice: 1999,
    price: 499,
    rating: 4.7,
    tags: ["GSAP", "Animations", "Frontend", "UX"],
    accentColor: "#7C3AED",
    language: "Telugu + English",
    slots: 100,
    mode: "Live + Recordings",
    forceClosed: true,
    description: "Build the kind of scroll-driven, cinematic animations you see on award-winning websites. GSAP is the secret weapon of elite frontend engineers.",
    curriculum: [
      { title: "Module 1 — GSAP Core", desc: "Tweens, timelines, and the API.", topics: ["gsap.to / from / fromTo", "Easing & Duration", "Stagger Animations", "Callbacks"] },
      { title: "Module 2 — Timelines & Sequencing", desc: "Orchestrate multi-step animations.", topics: ["Timeline Creation", "Labels & Position", "Nested Timelines", "Pause / Play / Reverse"] },
      { title: "Module 3 — ScrollTrigger", desc: "Make pages come alive as user scrolls.", topics: ["Basic ScrollTrigger", "Scrub & Pin", "Batch Animations", "Horizontal Scrolling"] },
      { title: "Module 4 — Real-World Projects", desc: "3 portfolio-ready animation builds.", topics: ["Hero Text Reveal", "Scroll-Driven Section", "Page Transition", "Performance Tips"] },
      { title: "Interview Kit", desc: "Frontend interview prep for animation roles.", topics: ["CSS vs JS Animation Q&A", "Performance Questions", "Portfolio Review", "Live Code Challenge"], isBonus: true },
    ],
  },
  {
    id: "grptch04",
    title: "Figma for Real-World UI/UX",
    image: GRPFigma,
    instructor: "Sruthi Pasupuleti & Eshwar Nagaraj",
    originalPrice: 1999,
    price: 499,
    rating: 4.7,
    tags: ["Figma", "UI/UX", "Design Systems"],
    accentColor: "#DB2777",
    language: "Telugu + English",
    slots: 100,
    mode: "Live + Recordings",
    forceClosed: true,
    description: "Go beyond wireframes. Design production-ready interfaces used in real startups — auto-layout, design tokens, components, and handoff workflows.",
    curriculum: [
      { title: "Module 1 — Figma Fundamentals", desc: "Frames, constraints, grids, workspace.", topics: ["Frame vs Group", "Constraints & Grids", "Typography", "Color Styles"] },
      { title: "Module 2 — Auto Layout Mastery", desc: "Responsive components.", topics: ["Horizontal & Vertical Stacks", "Nested Auto Layout", "Min/Max Width", "Absolute Positioning"] },
      { title: "Module 3 — Design Systems", desc: "Tokens, variants, documentation.", topics: ["Component Variants", "Design Tokens", "Icon Libraries", "Documentation"] },
      { title: "Module 4 — Real Startup Project", desc: "End-to-end SaaS dashboard design.", topics: ["Wireframe → High Fidelity", "Prototyping & Flows", "Dev Handoff", "Presentation Deck"] },
      { title: "Interview Kit", desc: "40+ design interview questions.", topics: ["Design Critique", "Case Study Walkthroughs", "Portfolio Review", "Mock Design Interview"], isBonus: true },
    ],
  },
  {
    id: "grptch05",
    title: "Core Java Foundations",
    image: GRPJava,
    instructor: "Eshwar Nagaraj",
    originalPrice: 2499,
    price: 499,
    rating: 4.6,
    tags: ["Java", "OOPS", "JVM", "DSA"],
    accentColor: "#D97706",
    language: "Telugu + English",
    slots: 100,
    mode: "Live + Recordings",
    forceClosed: true,
    description: "The most thorough Java bootcamp for interview success — JVM internals, collections, threading, and coding patterns that crack product companies.",
    curriculum: [
      { title: "Module 1 — Java & JVM", desc: "How Java actually executes code.", topics: ["Data Types & Operators", "JVM vs JDK vs JRE", "Memory Model", "GC Basics"] },
      { title: "Module 2 — OOPS in Depth", desc: "The 4 pillars done right.", topics: ["Encapsulation, Inheritance, Polymorphism", "Abstract vs Interface", "SOLID Principles", "Design Patterns"] },
      { title: "Module 3 — Collections & Generics", desc: "Data structures in every Java interview.", topics: ["List, Set, Map internals", "Comparable vs Comparator", "Generics", "Streams"] },
      { title: "Module 4 — Coding Practice", desc: "50 curated problems solved live.", topics: ["Array & String Patterns", "Sliding Window", "Recursion", "HashMap Patterns"] },
      { title: "Interview Kit", desc: "Top 60 Java interview questions.", topics: ["Core Java Q&A", "OOPS MCQ Sheet", "Coding Challenge Set", "Mock Technical Interview"], isBonus: true },
    ],
  },
  {
    id: "grptch06",
    title: "Git & GitHub for Real Teams",
    image: GRPGit,
    instructor: "Sowmya Ranjan",
    originalPrice: 1799,
    price: 499,
    rating: 4.6,
    tags: ["Git", "GitHub", "DevOps", "Collaboration"],
    accentColor: "#16A34A",
    language: "Telugu + English",
    slots: 100,
    mode: "Live + Recordings",
    forceClosed: true,
    description: "Git the way engineering teams actually use it — PR workflows, code reviews, CI/CD basics, and conflict resolution under real pressure.",
    curriculum: [
      { title: "Module 1 — Git Fundamentals", desc: "Commits as snapshots.", topics: ["Init, Clone, Stage, Commit", "Git Object Model", ".gitignore", "Undoing Changes"] },
      { title: "Module 2 — Branching & Merging", desc: "Every strategy used in real teams.", topics: ["Branch Strategies", "Merge vs Rebase vs Squash", "Cherry-Pick", "Tags & Releases"] },
      { title: "Module 3 — GitHub Workflows", desc: "Full GitHub toolkit.", topics: ["Fork & PR Model", "Code Review", "GitHub Actions Basics", "Branch Protection"] },
      { title: "Module 4 — Conflict Resolution", desc: "Handle merge conflicts like a senior dev.", topics: ["Reading Conflict Markers", "VS Code Merge Editor", "Rebase Conflicts", "Team Simulation"] },
      { title: "Interview Kit", desc: "Git scenario questions + cheat sheet.", topics: ["Top 30 Git Scenarios", "Commands Cheat Sheet", "CI/CD Intro Q&A", "Live GitHub Walkthrough"], isBonus: true },
    ],
  },
  {
    id: "grptch07",
    title: "Flask Backend APIs",
    image: GRPFlask,
    instructor: "Murali Kumar",
    originalPrice: 2299,
    price: 499,
    rating: 4.6,
    tags: ["Python", "Flask", "REST", "Backend"],
    accentColor: "#0891B2",
    language: "Telugu + English",
    slots: 100,
    mode: "Live + Recordings",
    forceClosed: true,
    description: "Build production-grade REST APIs using Flask — routing, auth, database integration, and deployment. Fastest path from Python basics to backend dev.",
    curriculum: [
      { title: "Module 1 — Flask Fundamentals", desc: "App factory, routing, blueprints.", topics: ["App Factory Pattern", "Routing & URL Rules", "Request/Response Objects", "Blueprints"] },
      { title: "Module 2 — REST API Design", desc: "Industry standard API design.", topics: ["HTTP Methods & Status Codes", "Serialization with Marshmallow", "Pagination & Filtering", "Error Handling"] },
      { title: "Module 3 — Database Integration", desc: "SQLAlchemy ORM.", topics: ["SQLAlchemy Models", "CRUD Operations", "Relationships & Joins", "Alembic Migrations"] },
      { title: "Module 4 — Auth & Deployment", desc: "JWT and cloud deploy.", topics: ["JWT Auth Flow", "RBAC", "Deploy on Render", "Env Variables & Secrets"] },
      { title: "Interview Kit", desc: "Backend interview prep.", topics: ["Top 50 Backend Qs", "REST API Design Exercise", "System Design Primer", "Mock Backend Interview"], isBonus: true },
    ],
  },
  {
    id: "grptch08",
    title: "GenAI Foundations",
    image: GRPGenAI,
    instructor: "Chaitanya Reddy",
    originalPrice: 2999,
    price: 499,
    rating: 4.8,
    tags: ["GenAI", "LLMs", "Prompt Engineering", "AI"],
    accentColor: "#059669",
    language: "Telugu + English",
    slots: 100,
    mode: "Live + Recordings",
    forceClosed: true,
    description: "The most practical GenAI course available — how LLMs actually work, prompt engineering mastery, and real AI-powered apps for your portfolio.",
    curriculum: [
      { title: "Module 1 — How GenAI Actually Works", desc: "No fluff — transformers, tokens, embeddings.", topics: ["Transformer Architecture", "Tokenization & Context Window", "Temperature & Sampling", "Model Comparison"] },
      { title: "Module 2 — Prompt Engineering", desc: "Highest-ROI AI skill right now.", topics: ["Zero-shot, Few-shot, Chain-of-Thought", "Structured Output Prompting", "Prompt Chaining", "System Prompts"] },
      { title: "Module 3 — Building with LLM APIs", desc: "Real apps on OpenAI and Anthropic.", topics: ["OpenAI & Anthropic SDK", "Chat Completion API", "Function Calling", "Streaming Responses"] },
      { title: "Module 4 — Real-World Projects", desc: "3 portfolio AI projects.", topics: ["RAG (Retrieval Augmented Generation)", "Vector Databases Intro", "LangChain Basics", "Deploy AI Apps"] },
      { title: "Interview Kit", desc: "GenAI interview prep.", topics: ["50 GenAI Interview Questions", "AI System Design", "Prompt Engineering Assessment", "Live AI Mock Interview"], isBonus: true },
    ],
  },
];

const FAQS = [
  { q: "Is this beginner-friendly?", a: "100%. No prior React or Node knowledge needed. We have a refresher on HTML, CSS, and JS built right into the course. If you can write a basic webpage, you're ready." },
  { q: "Are sessions live or recorded?", a: "Both. Every session is live with the instructor. Recordings are provided so you can revisit anytime. You'll never miss a concept." },
  { q: "What's included in the Interview Prep Kit?", a: "Top Q&A sets, coding challenges, resume review, LinkedIn profile guidance, and a live mock interview session with detailed feedback." },
  { q: "What language are sessions in?", a: "Telugu + English. We switch naturally based on what makes the concept clearest. No one left behind." },
  { q: "Will I get a real project to work on?", a: "Yes — not a to-do list. A real-world client use-case guided by the GrapplTech team. It goes on your GitHub and your resume." },
  { q: "How is the combo different from buying separately?", a: "The combo gives you React + Full Backend in one month. You also get LinkedIn optimisation, resume prep, and mock interviews. It's the whole package." },
  { q: "How many seats are available?", a: "100 slots per cohort. We keep batches small to give every student real attention. Once it's full, it's full." },
  { q: "Can I get a refund?", a: "Attend the first session. If it's not for you, request a refund within 24 hours. Full refund, no questions." },
];

/* ─── Icons ──────────────────────────────────────────────────────────────── */
const IconCheck = ({ size = 12, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color || "currentColor"} strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const IconStar = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="#F59E0B" stroke="#F59E0B" strokeWidth="1">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);
const IconArrow = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
  </svg>
);
const IconClose = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const IconPlus  = ({ size = 12 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>;
const IconMinus = ({ size = 12 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12" /></svg>;
const IconLock  = ({ size = 12 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>;

/* ─── Infinite Scroll Marquee ─────────────────────────────────────────────── */
const SCROLL_ITEMS = [
  "AI can write code, but it can't build your career",
  "Full-stack developers are irreplaceable in the AI era",
  "Real projects beat AI-generated tutorials every time",
  "Companies hire builders, not just certificate holders",
  "React + Backend = the combo AI cannot replace",
  "Your GitHub portfolio speaks louder than any AI prompt",
  "One month to full-stack — AI can't speedrun that for you",
  "Client project on resume > 100 online certificates",
  "Stop watching, start building real projects",
  "Deploy-ready skills are the ultimate job security",
];

const BADGE_META = [
  { image: BadgeTechGrandmaster, title: "Tech Grandmaster", blurb: "Awarded for broad technical depth across full product execution." },
  { image: BadgeCodeFusionist, title: "Code Fusionist", blurb: "Recognises engineers who connect frontend, backend, and systems thinking." },
  { image: BadgeAlgoAdept, title: "Algo Adept", blurb: "Marks strong problem-solving and interview-grade algorithmic reasoning." },
  { image: BadgePixelCrafter, title: "Pixel Crafter", blurb: "For students who obsess over visual polish and product-level UI craft." },
  { image: BadgeFrontendStrategist, title: "Frontend Strategist", blurb: "Given to builders who can turn UX thinking into responsive interfaces." },
  { image: BadgeComponentCommander, title: "Component Commander", blurb: "Represents mastery in reusable component architecture and UI systems." },
];

function InfiniteScrollBanner({ isDark }) {
  const text = SCROLL_ITEMS.map((t) => `  ✦  ${t}`).join("    ");
  const doubled = text + text;
  return (
    <div style={{
      overflow: "hidden",
      whiteSpace: "nowrap",
      padding: "14px 0",
      borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`,
      borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`,
      background: isDark ? "rgba(2,142,196,0.04)" : "rgba(2,142,196,0.03)",
      marginTop: 48,
      marginBottom: 8,
    }}>
      <div className="tiw-marquee-track">
        <div className="tiw-marquee-content" style={{
          display: "inline-block",
          fontSize: "clamp(12px, 1.6vw, 14px)",
          fontWeight: 500,
          color: isDark ? "rgba(255,255,255,0.42)" : "rgba(0,0,0,0.38)",
          letterSpacing: "0.01em",
        }}>
          {doubled}
        </div>
      </div>
    </div>
  );
}

/* ─── Curriculum Modal ───────────────────────────────────────────────────── */
function CurriculumModal({ course, onClose, isDark }) {
  const [openMod, setOpenMod] = useState(0);
  if (!course) return null;

  const bg     = isDark ? "#0A0A14" : "#ffffff";
  const border = isDark ? "rgba(255,255,255,0.08)" : "#E2E8F0";
  const text   = isDark ? "rgba(255,255,255,0.93)" : "#0F172A";
  const muted  = isDark ? "rgba(255,255,255,0.48)" : "#64748B";
  const divClr = isDark ? "rgba(255,255,255,0.06)" : "#F1F5F9";
  const rowBg  = isDark ? "rgba(255,255,255,0.04)" : "#F8FAFC";

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 9999, background: isDark ? "rgba(0,0,0,0.8)" : "rgba(0,0,0,0.45)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 16 }}
        transition={{ duration: 0.28, ease: [0.25, 0.46, 0.45, 0.94] }}
        onClick={e => e.stopPropagation()}
        style={{ background: bg, border: `1px solid ${border}`, borderRadius: 20, width: "100%", maxWidth: 600, maxHeight: "88vh", overflowY: "auto", position: "relative", boxShadow: isDark ? "0 40px 80px rgba(0,0,0,0.7)" : "0 40px 80px rgba(0,0,0,0.16)" }}
      >
        <div style={{ height: 3, background: `linear-gradient(90deg, ${course.accentColor}, ${course.accentColor}44)`, borderRadius: "20px 20px 0 0" }} />
        <button onClick={onClose} style={{ position: "absolute", top: 16, right: 16, width: 30, height: 30, borderRadius: "50%", background: isDark ? "rgba(255,255,255,0.07)" : "#F1F5F9", border: `1px solid ${border}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: muted }}>
          <IconClose />
        </button>

        <div style={{ padding: "24px 28px 20px" }}>
          <span style={{ display: "inline-block", background: course.accentColor + "18", color: course.accentColor, fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", padding: "3px 10px", borderRadius: 4, border: `1px solid ${course.accentColor}28`, marginBottom: 10 }}>
            Full Curriculum
          </span>
          <h2 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: "clamp(1.2rem, 3vw, 1.6rem)", fontWeight: 800, color: text, margin: "0 0 6px", lineHeight: 1.2 }}>{course.title}</h2>
          <p style={{ fontSize: 13, color: muted, margin: "0 0 20px" }}>By <strong style={{ color: course.accentColor }}>{course.instructor}</strong> &nbsp;·&nbsp; {[...Array(5)].map((_, i) => <IconStar key={i} size={11} />)} {course.rating}</p>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 24 }}>
            {[["Live Sessions", "Every day, real instructor"], ["Recordings Provided - Lifetime", "Watch anytime"], ["Interview Prep Kit", "Included"], ["Project + Certificate", "Portfolio-ready"]].map(([label, sub]) => (
              <div key={label} style={{ background: rowBg, border: `1px solid ${border}`, borderRadius: 10, padding: "10px 14px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                  <span style={{ color: course.accentColor }}><IconCheck size={11} color={course.accentColor} /></span>
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: text }}>{label}</span>
                </div>
                <p style={{ fontSize: 11, color: muted, margin: "3px 0 0 19px" }}>{sub}</p>
              </div>
            ))}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {course.curriculum.map((mod, i) => (
              <div key={i} style={{ border: `1.5px solid ${openMod === i ? course.accentColor + "55" : border}`, borderRadius: 12, overflow: "hidden", transition: "border-color 0.2s", boxShadow: openMod === i ? `0 0 0 3px ${course.accentColor}10` : "none" }}>
                <button
                  onClick={() => setOpenMod(openMod === i ? null : i)}
                  style={{ width: "100%", textAlign: "left", padding: "14px 18px", background: "transparent", border: "none", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, cursor: "pointer" }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ width: 24, height: 24, borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 900, background: mod.isBonus ? "linear-gradient(135deg,#F59E0B,#D97706)" : openMod === i ? course.accentColor : course.accentColor + "18", color: (openMod === i || mod.isBonus) ? "#fff" : course.accentColor, flexShrink: 0 }}>
                      {mod.isBonus ? "★" : String(i + 1).padStart(2, "0")}
                    </span>
                    <span style={{ fontSize: 13.5, fontWeight: 700, color: text, lineHeight: 1.3 }}>{mod.title}</span>
                  </div>
                  <span style={{ color: muted, flexShrink: 0 }}>{openMod === i ? <IconMinus /> : <IconPlus />}</span>
                </button>
                <AnimatePresence initial={false}>
                  {openMod === i && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }} style={{ overflow: "hidden" }}>
                      <div style={{ padding: "0 18px 16px 18px", borderTop: `1px solid ${divClr}` }}>
                        <p style={{ fontSize: 13, color: muted, lineHeight: 1.75, margin: "12px 0 10px" }}>{mod.desc}</p>
                        <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                          {mod.topics.map((t, j) => (
                            <div key={j} style={{ display: "flex", alignItems: "center", gap: 9 }}>
                              <span style={{ color: course.accentColor, flexShrink: 0 }}><IconCheck size={11} color={course.accentColor} /></span>
                              <span style={{ fontSize: 13, color: text }}>{t}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>

        {/* All seats filled — no Razorpay button shown */}
        <div style={{ padding: "16px 28px 28px" }}>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <div style={{ width: "100%", maxWidth: 320, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, background: isDark ? "rgba(255,255,255,0.06)" : "#F1F5F9", color: muted, fontWeight: 800, fontSize: 13, padding: "13px 18px", borderRadius: 12, border: `1px solid ${border}` }}>
              <IconLock size={11} /> Seats Filled
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ─── Course Card — all permanently closed (seats filled) ────────────────── */
function CourseCard({ meta, apiData, index, onCurriculum, isDark }) {
  const ref    = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });

  const ratingsCount = apiData?.ratings;

  const bg     = isDark ? "rgba(255,255,255,0.03)" : "#ffffff";
  const border = isDark ? "rgba(255,255,255,0.07)" : "#E2E8F0";
  const text   = isDark ? "rgba(255,255,255,0.93)" : "#0F172A";
  const muted  = isDark ? "rgba(255,255,255,0.48)" : "#64748B";
  const tagBg  = isDark ? "rgba(255,255,255,0.06)" : "#F1F5F9";

  const discountPct = Math.round((1 - meta.price / meta.originalPrice) * 100);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 32 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.06, ease: [0.16, 1, 0.3, 1] }}
      style={{ background: bg, border: `1px solid ${border}`, borderRadius: 18, overflow: "hidden", display: "flex", flexDirection: "column", position: "relative", boxShadow: isDark ? "0 2px 16px rgba(0,0,0,0.4)" : "0 1px 8px rgba(0,0,0,0.06)" }}
    >
      {/* Seats Filled badge — always shown */}
      <div style={{ position: "absolute", top: 14, right: 14, zIndex: 3, display: "flex", alignItems: "center", gap: 5, background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)", color: "#fff", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", padding: "4px 10px", borderRadius: 999 }}>
        <IconLock size={10} /> Seats Filled
      </div>

      <div style={{ height: 190, overflow: "hidden", position: "relative", flexShrink: 0 }}>
        <img
          src={meta.image}
          alt={meta.title}
          style={{ width: "100%", height: "100%", objectFit: "cover", filter: "grayscale(60%) brightness(0.7)" }}
        />
        <div style={{ position: "absolute", inset: 0, background: isDark ? "linear-gradient(to top, #0A0A14 0%, transparent 55%)" : "linear-gradient(to top, #fff 0%, transparent 55%)" }} />
        <div style={{ position: "absolute", bottom: 12, left: 14 }}>
          <span style={{ background: meta.accentColor + "22", border: `1.5px solid ${meta.accentColor}55`, color: meta.accentColor, fontSize: 10, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", padding: "3px 10px", borderRadius: 999, backdropFilter: "blur(8px)" }}>{meta.tags[0]}</span>
        </div>
      </div>

      <div style={{ padding: "18px 20px 20px", display: "flex", flexDirection: "column", gap: 12, flex: 1 }}>
        <div>
          <h3 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: "clamp(0.95rem, 2.5vw, 1.1rem)", fontWeight: 800, color: text, margin: "0 0 4px", lineHeight: 1.22 }}>{meta.title}</h3>
          <p style={{ fontSize: 12, color: muted, margin: 0 }}>By <strong style={{ color: meta.accentColor }}>{meta.instructor}</strong></p>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
          {[...Array(5)].map((_, i) => <IconStar key={i} size={12} />)}
          <span style={{ fontSize: 12.5, fontWeight: 700, color: text }}>{meta.rating}</span>
          {ratingsCount && <span style={{ fontSize: 11.5, color: muted }}>({ratingsCount} ratings)</span>}
          <span style={{ fontSize: 11, color: muted }}>· {meta.mode}</span>
        </div>

        <p style={{ fontSize: 13, color: muted, lineHeight: 1.7, margin: 0, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{meta.description}</p>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
          {meta.tags.map(t => <span key={t} style={{ fontSize: 10.5, fontWeight: 600, padding: "3px 9px", borderRadius: 5, background: tagBg, border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#E2E8F0"}`, color: muted }}>{t}</span>)}
        </div>

        <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
          <span style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: "1.7rem", fontWeight: 900, color: meta.accentColor, lineHeight: 1 }}>₹{meta.price}</span>
          <span style={{ fontSize: 13, color: muted, textDecoration: "line-through" }}>₹{meta.originalPrice}</span>
          <span style={{ fontSize: 10, fontWeight: 800, background: "rgba(5,150,105,0.12)", color: "#059669", border: "1px solid rgba(5,150,105,0.25)", padding: "2px 8px", borderRadius: 4 }}>{discountPct}% OFF</span>
        </div>

        <div style={{ flex: 1 }} />

        <div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
          {/* Enroll replaced with Seats Filled */}
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, background: tagBg, color: muted, fontWeight: 700, fontSize: 13, padding: "10px 0", borderRadius: 10, border: `1px solid ${isDark ? "rgba(255,255,255,0.07)" : "#E2E8F0"}` }}>
            <IconLock size={11} /> Seats Filled
          </div>
          <button
            onClick={() => onCurriculum(meta)}
            style={{ padding: "10px 14px", borderRadius: 10, background: "transparent", border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "#E2E8F0"}`, color: muted, fontSize: 12.5, fontWeight: 600, cursor: "pointer", transition: "all 0.18s", whiteSpace: "nowrap" }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = meta.accentColor + "55"; e.currentTarget.style.color = meta.accentColor; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = isDark ? "rgba(255,255,255,0.1)" : "#E2E8F0"; e.currentTarget.style.color = muted; }}
          >
            Curriculum
          </button>
        </div>
      </div>
    </motion.div>
  );
}

/* ─── FAQ Item ───────────────────────────────────────────────────────────── */
function FaqItem({ q, a, index, isOpen, onToggle, isDark }) {
  const bg     = isDark ? "rgba(255,255,255,0.02)" : "#ffffff";
  const border = isDark ? "rgba(255,255,255,0.07)" : "#E2E8F0";
  const text   = isDark ? "rgba(255,255,255,0.93)" : "#0F172A";
  const muted  = isDark ? "rgba(255,255,255,0.48)" : "#64748B";

  return (
    <div style={{ background: bg, border: `1.5px solid ${isOpen ? PRIMARY + "55" : border}`, borderRadius: 14, overflow: "hidden", transition: "border-color 0.2s", boxShadow: isOpen ? `0 0 0 3px ${PRIMARY}12` : "none" }}>
      <button
        onClick={onToggle}
        style={{ width: "100%", textAlign: "left", padding: "16px 20px", background: "transparent", border: "none", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 14, cursor: "pointer" }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, flex: 1 }}>
          <span style={{ width: 26, height: 26, borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 900, background: isOpen ? PRIMARY : PRIMARY + "18", color: isOpen ? "#fff" : PRIMARY, flexShrink: 0, marginTop: 1 }}>
            {String(index + 1).padStart(2, "0")}
          </span>
          <span style={{ fontSize: 14, fontWeight: 700, color: text, lineHeight: 1.4 }}>{q}</span>
        </div>
        <span style={{ color: isOpen ? PRIMARY : muted, flexShrink: 0, marginTop: 2 }}>{isOpen ? <IconMinus /> : <IconPlus />}</span>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }} style={{ overflow: "hidden" }}>
            <p style={{ fontSize: 14, color: muted, lineHeight: 1.82, margin: 0, padding: "0 20px 18px 58px" }}>{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ─── Main Page ──────────────────────────────────────────────────────────── */
export default function TechInWeek() {
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";

  const [apiMap, setApiMap]         = useState({});
  const [loading, setLoading]       = useState(true);
  const [openFaq, setOpenFaq]       = useState(null);
  const [currCourse, setCurrCourse] = useState(null);

  useEffect(() => {
    fetch(API_URL)
      .then(r => r.json())
      .then(data => {
        const map = {};
        data.forEach(row => { map[row.course_id] = row; });
        setApiMap(map);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const pageBg  = isDark ? "#06060E" : "#F0F4F8";
  const text    = isDark ? "rgba(255,255,255,0.93)" : "#0F172A";
  const muted   = isDark ? "rgba(255,255,255,0.48)" : "#64748B";
  const border  = isDark ? "rgba(255,255,255,0.07)" : "#E2E8F0";
  const heroBord = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.07)";

  return (
    <>
      <style>{`
        ${FONT_LINK}
        *, *::before, *::after { box-sizing: border-box; }
        .tiw-page { font-family: 'DM Sans', sans-serif; -webkit-font-smoothing: antialiased; }
        @keyframes tiw-marquee-scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .tiw-marquee-track {
          display: flex;
          width: max-content;
          animation: tiw-marquee-scroll 60s linear infinite;
        }
        .tiw-marquee-track:hover { animation-play-state: paused; }
        @keyframes tiw-shimmer {
          0% { background-position: -400px 0; }
          100% { background-position: 400px 0; }
        }
        .tiw-skeleton { background: linear-gradient(90deg, transparent, rgba(255,255,255,0.07), transparent); background-size: 400px; animation: tiw-shimmer 1.8s infinite; }
        .tiw-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 16px; }
        @media (max-width: 480px) { .tiw-grid { grid-template-columns: 1fr; } }
        @keyframes tiw-badge-marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .tiw-badge-marquee {
          display: flex;
          width: max-content;
          animation: tiw-badge-marquee 28s linear infinite;
        }
        .tiw-badge-marquee:hover { animation-play-state: paused; }
        @keyframes tiw-badge-float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
        .tiw-badge-card { animation: tiw-badge-float 4.5s ease-in-out infinite; }
      `}</style>

      <AnimatePresence>
        {currCourse && <CurriculumModal course={currCourse} onClose={() => setCurrCourse(null)} isDark={isDark} />}
      </AnimatePresence>

      <div className="tiw-page" style={{ background: pageBg, minHeight: "100vh", color: text }}>

        {/* ── HERO ────────────────────────────────────────────────────────── */}
        <div style={{ textAlign: "center", padding: "88px 20px 64px", borderBottom: `1px solid ${heroBord}`, position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: "-10%", left: "-5%", width: 500, height: 500, borderRadius: "50%", background: isDark ? `${PRIMARY}0A` : `${PRIMARY}06`, filter: "blur(80px)", pointerEvents: "none" }} />
          <div style={{ position: "absolute", top: "20%", right: "-8%", width: 400, height: 400, borderRadius: "50%", background: isDark ? "rgba(245,158,11,0.06)" : "rgba(245,158,11,0.04)", filter: "blur(80px)", pointerEvents: "none" }} />

          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55 }} style={{ position: "relative" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 7, background: isDark ? `${PRIMARY}12` : `${PRIMARY}08`, border: `1px solid ${isDark ? `${PRIMARY}30` : `${PRIMARY}20`}`, color: PRIMARY, fontSize: 10, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", padding: "5px 14px", borderRadius: 5, marginBottom: 24 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: PRIMARY }} /> 7-Day Intensive Programs
            </span>

            <h1 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: "clamp(2.8rem, 8vw, 6.5rem)", fontWeight: 800, lineHeight: 0.95, margin: "0 0 12px", letterSpacing: "-0.025em", color: text }}>
              Tech In <span style={{ background: `linear-gradient(135deg, ${PRIMARY}, #38BDF8)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Week</span>
            </h1>
            <AnimatedUnderline1 />

            <p style={{ fontSize: "clamp(14px, 2.2vw, 18px)", color: muted, maxWidth: 520, margin: "18px auto 32px", lineHeight: 1.82 }}>
              A complete, job-ready tech skill in 7 days. Live sessions, real projects, interview kit — starting at ₹499.
            </p>

            <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "10px 28px" }}>
              {[["6,000+", "Alumni"], ["Industry", "Certificate"], ["Live", "Q&A Sessions"], ["Placement", "Support"]].map(([v, l]) => (
                <div key={l} style={{ display: "flex", alignItems: "center", gap: 7 }}>
                  <span style={{ color: PRIMARY }}><IconCheck size={13} color={PRIMARY} /></span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: muted }}><strong style={{ color: text }}>{v}</strong> {l}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 18px" }}>

          {/* ── INFINITE SCROLL BANNER ─────────────────────────────────────── */}
          <InfiniteScrollBanner isDark={isDark} />

          {/* ── PAST COURSES GRID ─────────────────────────────────────────── */}
          <div style={{ marginTop: 80 }}>
            <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: muted, margin: "0 0 5px" }}>Previous Batches</p>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
              <h2 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: "clamp(1.4rem, 3vw, 1.9rem)", fontWeight: 800, color: text, margin: 0 }}>
                7-Day Intensives
              </h2>
              <span style={{ fontSize: 13, color: muted }}>All seats filled · Check back for the next cohort</span>
            </div>
            {/* Seats filled notice */}
            <div style={{ background: isDark ? "rgba(255,255,255,0.03)" : "#FEF9EE", border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(245,158,11,0.22)"}`, borderRadius: 12, padding: "12px 18px", display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
              <IconLock size={13} color="#D97706" />
              <p style={{ fontSize: 13, color: isDark ? "rgba(255,255,255,0.55)" : "#92400E", margin: 0, lineHeight: 1.5 }}>
                <strong>All 7-Day batches are currently full.</strong> Registrations for the next cohort will open soon. Meanwhile, explore our new GOD Level programs above.
              </p>
            </div>

            {loading ? (
              <div className="tiw-grid">
                {Array(6).fill(0).map((_, i) => (
                  <div key={i} style={{ background: isDark ? "rgba(255,255,255,0.03)" : "#fff", border: `1px solid ${border}`, borderRadius: 18, overflow: "hidden" }}>
                    <div style={{ height: 190, background: isDark ? "rgba(255,255,255,0.06)" : "#F1F5F9" }} className="tiw-skeleton" />
                    <div style={{ padding: 20 }}>
                      {[80, 55, 40, 90, 60].map((w, j) => (
                        <div key={j} style={{ height: 11, background: isDark ? "rgba(255,255,255,0.06)" : "#F1F5F9", borderRadius: 999, width: `${w}%`, marginBottom: 10 }} className="tiw-skeleton" />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="tiw-grid">
                {COURSE_META.map((meta, i) => (
                  <CourseCard key={meta.id} meta={meta} apiData={apiMap[meta.id]} index={i} onCurriculum={setCurrCourse} isDark={isDark} />
                ))}
              </div>
            )}
          </div>

          {/* ── TRUST STRIP ──────────────────────────────────────────────────── */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} style={{ marginTop: 72 }}>
            <div style={{ background: isDark ? "rgba(255,255,255,0.02)" : "#ffffff", border: `1px solid ${border}`, borderRadius: 18, padding: "32px 36px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 24 }}>
                {[
                  ["All-Inclusive", "No hidden charges"],
                  ["Live Sessions", "With recordings provided"],
                  ["Industry Certificate", "For every completer"],
                  ["Interview Kit", "In every course"],
                  ["Real Projects", "On your portfolio"],
                  ["Mock Interviews", "In combo bundle"],
                ].map(([label, sub]) => (
                  <div key={label} style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 6 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ width: 28, height: 28, borderRadius: 8, background: isDark ? `${PRIMARY}18` : `${PRIMARY}10`, border: `1px solid ${PRIMARY}25`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <IconCheck size={12} color={PRIMARY} />
                      </span>
                      <span style={{ fontSize: 13.5, fontWeight: 700, color: text }}>{label}</span>
                    </div>
                    <p style={{ fontSize: 12, color: muted, margin: "0 0 0 36px" }}>{sub}</p>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* ── BADGES ───────────────────────────────────────────────────────── */}
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} style={{ marginTop: 80 }}>
            <div style={{ textAlign: "center", marginBottom: 28 }}>
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: PRIMARY }}>GrapplTech Badges</span>
              <h2 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: "clamp(1.8rem, 5vw, 3.2rem)", fontWeight: 800, color: text, margin: "8px 0 10px", lineHeight: 1.05 }}>
                Earn badges that make your work visible
              </h2>
              <p style={{ fontSize: 14, color: muted, maxWidth: 760, margin: "0 auto", lineHeight: 1.8 }}>
                GrapplTech badges spotlight the strengths you prove through projects, performance, creativity, and engineering depth.
              </p>
            </div>

            <div style={{ overflow: "hidden", borderRadius: 22, border: `1px solid ${border}`, background: isDark ? "linear-gradient(135deg, rgba(255,255,255,0.03), rgba(255,255,255,0.02))" : "linear-gradient(135deg, #ffffff, #F8FAFC)", padding: "22px 0" }}>
              <div className="tiw-badge-marquee">
                {[...BADGE_META, ...BADGE_META].map((badge, i) => (
                  <div key={`${badge.title}-${i}`} style={{ width: 280, flexShrink: 0, padding: "0 12px" }}>
                    <div className="tiw-badge-card" style={{ height: "100%", background: isDark ? "rgba(255,255,255,0.04)" : "#ffffff", border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#E2E8F0"}`, borderRadius: 20, padding: "18px 18px 20px", boxShadow: isDark ? "0 10px 30px rgba(0,0,0,0.28)" : "0 10px 24px rgba(15,23,42,0.07)" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 150, marginBottom: 12 }}>
                        <img src={badge.image} alt={badge.title} style={{ width: "100%", maxWidth: 170, height: "auto", objectFit: "contain", filter: isDark ? "drop-shadow(0 10px 18px rgba(0,0,0,0.45))" : "drop-shadow(0 8px 16px rgba(2,142,196,0.16))" }} />
                      </div>
                      <h3 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: 18, fontWeight: 800, color: text, margin: "0 0 8px", lineHeight: 1.2 }}>{badge.title}</h3>
                      <p style={{ fontSize: 12.5, color: muted, margin: 0, lineHeight: 1.7 }}>{badge.blurb}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* ── FAQ ──────────────────────────────────────────────────────────── */}
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} style={{ marginTop: 80, maxWidth: 720, margin: "80px auto 0" }}>
            <div style={{ textAlign: "center", marginBottom: 36 }}>
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: PRIMARY }}>FAQs</span>
              <h2 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: "clamp(2rem, 6vw, 4.5rem)", fontWeight: 800, color: text, margin: "6px 0 0", lineHeight: 0.95, letterSpacing: "-0.02em" }}>
                Still<br /><span style={{ color: PRIMARY }}>wondering?</span>
              </h2>
              <p style={{ fontSize: 14, color: muted, marginTop: 14, lineHeight: 1.75 }}>Everything you need to know before you hit enrol. Still have questions? We're on WhatsApp.</p>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {FAQS.map((f, i) => (
                <FaqItem key={i} index={i} q={f.q} a={f.a} isOpen={openFaq === i} onToggle={() => setOpenFaq(openFaq === i ? null : i)} isDark={isDark} />
              ))}
            </div>
          </motion.div>

          {/* ── BOTTOM CTA ───────────────────────────────────────────────────── */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.45 }} style={{ marginTop: 64, marginBottom: 80 }}>
            <div style={{ background: isDark ? "linear-gradient(135deg, #0D1117 0%, #1a2332 100%)" : "linear-gradient(135deg, #0F172A 0%, #1a2332 100%)", borderRadius: 22, padding: "clamp(36px, 5vw, 72px)", textAlign: "center", color: "#fff", position: "relative", overflow: "hidden", boxShadow: "0 32px 80px rgba(0,0,0,0.4)" }}>
              <div style={{ position: "absolute", top: "-20%", left: "-8%", width: 450, height: 450, borderRadius: "50%", background: `${PRIMARY}18`, filter: "blur(80px)", pointerEvents: "none" }} />
              <div style={{ position: "absolute", bottom: "-20%", right: "-8%", width: 350, height: 350, borderRadius: "50%", background: "rgba(255,169,0,0.08)", filter: "blur(80px)", pointerEvents: "none" }} />
              <div style={{ position: "relative" }}>
                <span style={{ display: "inline-block", background: `${PRIMARY}22`, border: `1.5px solid ${PRIMARY}40`, color: PRIMARY, fontSize: 10, fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase", padding: "4px 14px", borderRadius: 4, marginBottom: 20 }}>
                  Limited Seats Per Cohort
                </span>
                <h2 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: "clamp(1.8rem, 5vw, 3.4rem)", fontWeight: 800, lineHeight: 1.1, margin: "0 0 14px", letterSpacing: "-0.02em" }}>
                  Learn a skill. Build a project.<br />
                  <span style={{ color: PRIMARY }}>Crack the interview.</span>
                </h2>
                <p style={{ fontSize: "clamp(13px, 2vw, 16px)", opacity: 0.72, maxWidth: 500, margin: "0 auto 28px", lineHeight: 1.8 }}>
                  Starting at ₹999. No hidden charges. Live instructor. Real certificate. Interview kit. Telugu + English.
                </p>
                <a href="#courses"
                  style={{ display: "inline-flex", alignItems: "center", gap: 8, background: PRIMARY, color: "#fff", fontWeight: 800, fontSize: 15, padding: "13px 32px", borderRadius: 12, textDecoration: "none", boxShadow: `0 8px 28px ${PRIMARY}55`, transition: "filter 0.2s, transform 0.2s" }}
                  onMouseEnter={e => { e.currentTarget.style.filter = "brightness(1.1)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                  onMouseLeave={e => { e.currentTarget.style.filter = "none"; e.currentTarget.style.transform = "none"; }}
                >
                  Browse GOD Level Courses <IconArrow />
                </a>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </>
  );
}